import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link }  from 'react-router-dom';
import { useAuth }      from '../context/AuthContext';
import DashboardNav     from '../components/dashboard/DashboardNav';
import SubscriptionBanner from '../components/dashboard/SubscriptionBanner';
import { apiFetch }     from '../lib/api';

const TIER_NAMES: Record<number, string> = {
  1: 'Thoughtful',
  2: 'Connected',
  3: 'Premium',
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-[20px] shadow-warm p-6 sm:p-8">
      <div className="mb-6">
        <h2 className="font-heading text-lg font-semibold text-navy">{title}</h2>
        {description && (
          <p className="mt-1 font-body text-sm text-gray-500">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Profile section ──────────────────────────────────────────────────────────

function ProfileSection() {
  const { profile, refreshProfile } = useAuth();
  const [firstName, setFirstName] = useState(profile?.first_name ?? '');
  const [lastName,  setLastName ] = useState(profile?.last_name  ?? '');
  const [phone,     setPhone    ] = useState(profile?.phone      ?? '');
  const [saving,    setSaving   ] = useState(false);
  const [saved,     setSaved    ] = useState(false);
  const [error,     setError    ] = useState('');

  // Sync if profile loads after mount
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name ?? '');
      setLastName (profile.last_name  ?? '');
      setPhone    (profile.phone      ?? '');
    }
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await apiFetch('/api/auth/profile', {
        method: 'PATCH',
        body:   JSON.stringify({ firstName, lastName, phone }),
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Profile" description="Your account name and contact info. Your phone number is used for 14-day card reminders via SMS.">
      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1">
              First name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-gray-700 mb-1">
              Last name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input-field w-full"
              required
            />
          </div>
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-gray-700 mb-1">
            Phone number
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 000-0000"
            className="input-field w-full"
          />
          <p className="mt-1 font-body text-xs text-gray-400">
            Used for SMS card reminders. Standard messaging rates may apply.
          </p>
        </div>

        {error && (
          <p className="font-body text-sm text-rose-600">{error}</p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary text-sm px-5 py-2 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {saved && (
            <p className="font-body text-sm text-sage">✓ Saved</p>
          )}
        </div>
      </form>
    </Section>
  );
}


// ─── Cancellation feedback modal ──────────────────────────────────────────────

const CANCEL_REASONS = [
  'Too expensive',
  'Not using it enough',
  'Missing a feature I need',
  'Technical issues',
  'Just testing it out',
  'Switching to another service',
  'Other',
];

function CancelFeedbackModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) {
  const [reason,    setReason]    = useState('');
  const [comment,   setComment]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,     setError]     = useState('');

  async function handleSubmit() {
    if (!reason) { setError('Please select a reason.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await apiFetch('/api/stripe/cancel-feedback', {
        method: 'POST',
        body:   JSON.stringify({ reason, comment }),
      });
    } catch {
      // Non-fatal — still open portal even if save fails
    } finally {
      setSubmitting(false);
      onConfirm();
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        backgroundColor: 'rgba(26,20,16,0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#fff', borderRadius: 24,
          boxShadow: '0 25px 80px rgba(0,0,0,0.2)',
          width: '100%', maxWidth: 440, padding: 28,
          display: 'flex', flexDirection: 'column', gap: 18,
        }}
      >
        <div>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: 18, fontWeight: 700, color: '#1A1410', marginBottom: 4 }}>
            Before you go…
          </p>
          <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 13, color: '#6B7280' }}>
            Help us improve by sharing why you're cancelling. This takes 20 seconds.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {CANCEL_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              style={{
                textAlign: 'left', padding: '10px 14px',
                borderRadius: 10, border: `1.5px solid ${reason === r ? '#1A1410' : '#E8E0D5'}`,
                backgroundColor: reason === r ? '#F8F5EE' : '#FAFAF9',
                fontFamily: '"Inter",Arial,sans-serif', fontSize: 13,
                color: reason === r ? '#1A1410' : '#6B7280',
                fontWeight: reason === r ? 600 : 400,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {reason === r ? '● ' : '○ '}{r}
            </button>
          ))}
        </div>

        <div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={3}
            placeholder="Anything else you'd like us to know? (optional)"
            style={{
              width: '100%', borderRadius: 10, border: '1.5px solid #E8E0D5',
              backgroundColor: '#FAFAF9', padding: '10px 12px',
              fontFamily: '"Inter",Arial,sans-serif', fontSize: 13,
              color: '#1A1410', resize: 'none', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <p style={{ fontFamily: '"Inter",Arial,sans-serif', fontSize: 12, color: '#DC2626' }}>{error}</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 100,
              border: 'none', backgroundColor: '#1A1410', color: '#fff',
              fontFamily: '"Inter",Arial,sans-serif', fontSize: 14, fontWeight: 600,
              cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Saving…' : 'Continue to cancel →'}
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '11px 0', borderRadius: 100,
              border: '1.5px solid #E8E0D5', backgroundColor: 'transparent',
              fontFamily: '"Inter",Arial,sans-serif', fontSize: 13, color: '#9CA3AF',
              cursor: 'pointer',
            }}
          >
            Never mind, keep my subscription
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Subscription section ─────────────────────────────────────────────────────

function SubscriptionSection() {
  const { profile } = useAuth();
  const [loadingPortal,   setLoadingPortal]   = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  if (!profile) return null;

  const status   = profile.subscription_status;
  const tierName = profile.tier ? TIER_NAMES[profile.tier] : null;

  let trialDaysLeft: number | null = null;
  if (status === 'trialing' && profile.trial_ends_at) {
    const diff = new Date(profile.trial_ends_at).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  // "Cancel at period end" — subscription is still active but will end on this date
  const cancelAt = profile.subscription_cancel_at
    ? new Date(profile.subscription_cancel_at)
    : null;
  const cancelAtFormatted = cancelAt
    ? cancelAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  async function openPortal() {
    setLoadingPortal(true);
    try {
      const json = await apiFetch<{ url: string }>('/api/stripe/create-portal-session', {
        method: 'POST',
        body:   JSON.stringify({ returnPath: '/account' }),
      });
      if (json.url) window.location.href = json.url;
    } finally {
      setLoadingPortal(false);
    }
  }

  function handleCancelClick() {
    setShowCancelModal(true);
  }

  function handleCancelConfirmed() {
    setShowCancelModal(false);
    openPortal();
  }

  return (
    <>
    {showCancelModal && (
      <CancelFeedbackModal
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirmed}
      />
    )}
    <Section title="Subscription" description="Manage your plan, payment method, and billing history.">

      {/* Cancel-at-period-end notice */}
      {cancelAtFormatted && status === 'active' && (
        <div className="mb-5 flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
          <span className="text-base flex-shrink-0">📅</span>
          <div>
            <p className="font-body text-sm font-semibold text-amber-900">
              Subscription ends {cancelAtFormatted}
            </p>
            <p className="font-body text-xs text-amber-700 mt-0.5">
              You have full access until then. Any cards scheduled to mail before that date will still be sent.
              Cards scheduled after that date will be cancelled.
            </p>
          </div>
        </div>
      )}

      {/* Current plan pill */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-body font-semibold ${
              status === 'active' && cancelAtFormatted ? 'bg-amber-100 text-amber-700' :
              status === 'active'   ? 'bg-sage/15 text-sage' :
              status === 'trialing' ? 'bg-gold/20 text-amber-700' :
              status === 'past_due' ? 'bg-rose-100 text-rose-700' :
              'bg-gray-100 text-gray-500'
            }`}>
              {status === 'active' && cancelAtFormatted ? `Cancels ${cancelAtFormatted}` :
               status === 'active'   ? '✓ Active' :
               status === 'trialing' ? '🕐 Free trial' :
               status === 'past_due' ? '⚠ Past due' :
               status === 'canceled' ? 'Cancelled' : 'No plan'}
            </span>
            {tierName && (
              <span className="font-body text-sm font-semibold text-navy">{tierName}</span>
            )}
          </div>

          {status === 'trialing' && trialDaysLeft !== null && (
            <p className="font-body text-sm text-gray-500">
              {trialDaysLeft === 0
                ? 'Trial ends today, add a payment method to continue.'
                : `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} remaining in your free trial.`}
            </p>
          )}
          {status === 'past_due' && (
            <p className="font-body text-sm text-rose-600">
              Your last payment failed. Update your card to restore access.
            </p>
          )}
          {status === 'canceled' && (
            <p className="font-body text-sm text-gray-500">
              Your subscription has ended. Resubscribe anytime to continue sending cards.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {(status === 'active' || status === 'trialing' || status === 'past_due') && (
            <button
              type="button"
              onClick={openPortal}
              disabled={loadingPortal}
              className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
            >
              {loadingPortal ? 'Loading…' : 'Manage billing'}
            </button>
          )}
          {(!status || status === 'canceled') && (
            <Link
              to="/pricing"
              className="btn-primary text-sm px-4 py-2"
            >
              View plans
            </Link>
          )}
          {/* Cancel subscription link — show for active/trialing unless already explicitly cancelled */}
          {(status === 'active' || status === 'trialing') && !(status === 'active' && cancelAtFormatted) && (
            <button
              type="button"
              onClick={handleCancelClick}
              className="font-body text-xs text-rose-400 hover:text-rose-600 transition-colors underline underline-offset-2 text-left"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {/* Feature highlights for current tier */}
      {profile.tier && (
        <div className="border-t border-gray-100 pt-5">
          <p className="font-body text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
            Your plan includes
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-body text-sm text-gray-600">
            {TIER_FEATURES[profile.tier].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <svg className="h-4 w-4 text-sage flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
    </>
  );
}

const TIER_FEATURES: Record<number, string[]> = {
  1: ['Up to 5 contacts', 'Personalised card messages', 'Standard card designs', '14-day preview window'],
  2: ['Up to 20 contacts', 'All occasion types', 'Up to 3 custom occasions', '14-day preview window'],
  3: ['Unlimited contacts', 'Unlimited custom occasions', '30-day preview window', 'Send a card now', 'Relationship Wrapped'],
};

// ─── Notification preferences ─────────────────────────────────────────────────

function NotificationsSection() {
  const { profile, refreshProfile } = useAuth();
  const [smsOptedOut,    setSmsOptedOut   ] = useState(profile?.sms_opted_out    ?? false);
  const [marketingOptIn, setMarketingOptIn] = useState(profile?.marketing_opt_in ?? false);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved ] = useState(false);

  async function save(updates: { smsOptedOut?: boolean; marketingOptIn?: boolean }) {
    setSaving(true);
    setSaved(false);
    try {
      await apiFetch('/api/auth/profile', {
        method: 'PATCH',
        body:   JSON.stringify({
          smsOptedOut:    updates.smsOptedOut    ?? smsOptedOut,
          marketingOptIn: updates.marketingOptIn ?? marketingOptIn,
        }),
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  function toggle(field: 'sms' | 'marketing') {
    if (field === 'sms') {
      const next = !smsOptedOut;
      setSmsOptedOut(next);
      save({ smsOptedOut: next });
    } else {
      const next = !marketingOptIn;
      setMarketingOptIn(next);
      save({ marketingOptIn: next });
    }
  }

  return (
    <Section title="Notifications" description="Choose how MomentSent can reach you.">
      <div className="space-y-5">
        <Toggle
          label="SMS reminders"
          description="Receive text messages 14 days and 48 hours before each card sends."
          checked={!smsOptedOut}
          disabled={saving}
          onChange={() => toggle('sms')}
        />
        <Toggle
          label="Marketing emails"
          description="Occasional tips, new features, and seasonal card ideas."
          checked={marketingOptIn}
          disabled={saving}
          onChange={() => toggle('marketing')}
        />

        <p className="font-body text-xs text-gray-400 border-t border-gray-100 pt-4">
          Transactional emails (card confirmations, billing receipts) are always sent regardless of these settings.
          {saved && <span className="text-sage ml-2">✓ Saved</span>}
        </p>
      </div>
    </Section>
  );
}

function Toggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label:       string;
  description: string;
  checked:     boolean;
  disabled:    boolean;
  onChange:    () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="font-body text-sm font-medium text-navy">{label}</p>
        <p className="font-body text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        disabled={disabled}
        className={`relative flex-shrink-0 h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage disabled:opacity-50 ${
          checked ? 'bg-sage' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
          style={{ marginTop: '2px' }}
        />
      </button>
    </div>
  );
}

// ─── Referral section ─────────────────────────────────────────────────────────

function ReferralSection() {
  const { profile } = useAuth();
  const [copied, setCopied] = useState(false);

  if (!profile?.referral_code) return null;

  const referralUrl = `${window.location.origin}/join?ref=${profile.referral_code}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      prompt('Copy your referral link:', referralUrl);
    }
  }

  return (
    <Section
      title="Referrals"
      description="Share your link, when a friend subscribes, you both get a $7.99 credit."
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5">
          <p className="font-body text-sm text-gray-600 truncate">{referralUrl}</p>
        </div>
        <button
          type="button"
          onClick={copy}
          className="flex-shrink-0 btn-primary text-sm px-4 py-2"
        >
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
      </div>

      <div className="flex items-center gap-6 border-t border-gray-100 pt-4">
        <div className="text-center">
          <p className="font-heading text-2xl font-bold text-navy">{profile.referral_count ?? 0}</p>
          <p className="font-body text-xs text-gray-500">friends referred</p>
        </div>
        <div className="text-center">
          <p className="font-heading text-2xl font-bold text-sage">
            ${((profile.referral_count ?? 0) * 7.99).toFixed(2)}
          </p>
          <p className="font-body text-xs text-gray-500">total credits earned</p>
        </div>
      </div>
    </Section>
  );
}

// ─── Danger zone ──────────────────────────────────────────────────────────────

function DangerZone() {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [deleting,   setDeleting  ] = useState(false);
  const [error,      setError     ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputVal, setInputVal]  = useState('');

  async function handleDelete() {
    if (inputVal !== 'delete my account') return;
    setDeleting(true);
    setError('');
    try {
      await apiFetch('/api/auth/account', { method: 'DELETE' });
      // Sign out and redirect home
      const { supabase } = await import('../lib/supabase');
      await supabase.auth.signOut();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setDeleting(false);
    }
  }

  return (
    <Section title="Danger zone">
      {!confirming ? (
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-body text-sm text-gray-700">Delete your account</p>
            <p className="font-body text-xs text-gray-400 mt-0.5">
              Permanently remove your account, all contacts, occasions, and billing data. This cannot be undone.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setConfirming(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            className="flex-shrink-0 font-body text-sm font-medium text-rose-600 border border-rose-200 rounded-xl px-4 py-2 hover:bg-rose-50 transition-colors"
          >
            Delete account
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="font-body text-sm text-gray-700">
            This will <strong>permanently delete</strong> your account, cancel your subscription, and remove all your data.
            Type <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">delete my account</code> to confirm.
          </p>
          <input
            ref={inputRef}
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            placeholder="delete my account"
            className="input-field w-full"
          />
          {error && <p className="font-body text-sm text-rose-600">{error}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || inputVal !== 'delete my account'}
              className="font-body text-sm font-semibold text-white bg-rose-600 rounded-xl px-5 py-2 hover:bg-rose-700 transition-colors disabled:opacity-40"
            >
              {deleting ? 'Deleting…' : 'Yes, permanently delete'}
            </button>
            <button
              type="button"
              onClick={() => { setConfirming(false); setInputVal(''); setError(''); }}
              className="font-body text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ─── AccountPage ──────────────────────────────────────────────────────────────

export default function AccountPage() {
  const { profile, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !profile) navigate('/login');
  }, [loading, profile, navigate]);

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-sage border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <DashboardNav />
      <SubscriptionBanner />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 pb-24 sm:pb-10 space-y-6">
        {/* Page header */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Account settings</h1>
          <p className="font-body text-sm text-gray-500 mt-1">{profile.email}</p>
        </div>

        <ProfileSection />
        <SubscriptionSection />
        <NotificationsSection />
        <ReferralSection />
        <DangerZone />
      </main>
    </div>
  );
}
